import { z } from 'zod';
import { CURRENCIES } from '../money/money';
import { REPORT_OPTION_FILTER_KEYS } from '../reports/report-metrics';
import { defineListQuery, listFilter } from './list-query.schemas';

/**
 * The filter bar every report shares (gitaumoses4/chukua#200): a period (`from`/`to`) or an as-of date, and the
 * truck, driver, customer and route a figure may be narrowed to. A report ignores a filter it does not declare
 * (`ReportSummary.filters`) rather than refusing it, so one URL can move between reports.
 */
const reportFilters = {
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  asOf: z.iso.date().optional(),
  customerId: listFilter(z.uuid()),
  truckId: listFilter(z.uuid()),
  driverId: listFilter(z.uuid()),
  originZone: listFilter(z.string().trim().min(1).max(50)),
  destinationZone: listFilter(z.string().trim().min(1).max(50)),
  currency: listFilter(z.enum(CURRENCIES)),
};

function hasOrderedPeriod({ from, to }: { from?: string; to?: string }): boolean {
  return !from || !to || from <= to;
}
const periodIssue = { message: '`from` must not be after `to`', path: ['from'] };

/** `GET /reports/:key` — the figures, one page of them. */
export const reportQuerySchema = defineListQuery({
  sortFields: ['label', 'value'],
  defaultSort: { sort: 'label', order: 'asc' },
  filters: reportFilters,
}).refine(hasOrderedPeriod, periodIssue);
export type ReportQuery = z.infer<typeof reportQuerySchema>;

/** `GET /reports/:key/records` — the records behind the figures, under the same filters. */
export const reportRecordsQuerySchema = defineListQuery({
  sortFields: ['date', 'amount'],
  defaultSort: { sort: 'date', order: 'desc' },
  filters: reportFilters,
}).refine(hasOrderedPeriod, periodIssue);
export type ReportRecordsQuery = z.infer<typeof reportRecordsQuerySchema>;

/** `GET /reports/options/:filter` — the choices for one filter's picker. */
export const reportOptionFilterSchema = z.enum(REPORT_OPTION_FILTER_KEYS);
/** `value` names the choices to label (the filter's current values), instead of searching. */
export const reportOptionsQuerySchema = z.object({ q: reportQuerySchema.shape.q, value: listFilter(z.string().trim().min(1).max(50)) });
export type ReportOptionsQuery = z.infer<typeof reportOptionsQuerySchema>;

/** Most rows one export may hold. Over it the request is refused with the count, never truncated: a file that silently stops short is worse than none. */
export const REPORT_EXPORT_MAX_ROWS = 50_000;

export const REPORT_EXPORT_KINDS = ['figures', 'records'] as const;
export type ReportExportKind = (typeof REPORT_EXPORT_KINDS)[number];

const EXPORT_SORTS: Record<ReportExportKind, readonly string[]> = { figures: ['label', 'value'], records: ['date', 'amount'] };
const EXPORT_DEFAULT_SORTS = { figures: { sort: 'label', order: 'asc' }, records: { sort: 'date', order: 'desc' } } as const;

/**
 * `GET /reports/:key/export` — the filters, search and sort of the screen it was opened from, but no `page`/`pageSize`:
 * a file holds every matching row. `sort` is checked against the kind, because figures and records sort by different fields.
 */
export const reportExportQuerySchema = z
  .object({
    kind: z.enum(REPORT_EXPORT_KINDS),
    q: reportQuerySchema.shape.q,
    sort: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    ...reportFilters,
  })
  .refine(hasOrderedPeriod, periodIssue)
  .refine(({ kind, sort }) => sort === undefined || EXPORT_SORTS[kind].includes(sort), {
    message: '`sort` is not a sort field of this kind of export',
    path: ['sort'],
  })
  .transform((query) => ({
    ...query,
    ...EXPORT_DEFAULT_SORTS[query.kind],
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.order ? { order: query.order } : {}),
  }));
export type ReportExportQuery = z.infer<typeof reportExportQuerySchema>;
